import React, { useState, useEffect, useMemo } from 'react';
import { TrashIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/solid';
import { promoCodesApi, apiUrl, authFetch, authService } from '../utils/api';
import { stripSarPrice } from '../utils/coursePrice';
import { useUser } from '../contexts/UserContext';
import * as XLSX from 'xlsx';

interface PromoCode {
  id: string;
  code: string;
  type: 'SingleUse' | 'MultiUse' | 1 | 2; // API returns 1=MultiUse, 2=SingleUse
  discountType: 'Percentage' | 'FixedAmount' | 1 | 2; // API returns 1=FixedAmount, 2=Percentage
  discountValue: number;
  expirationDate: string;
  maxGlobalUsage: number | null;
  maxUsagePerStudent: number | null;
  isActive: boolean;
  createdDate: string;
  createdBy: string;
  description: string | null;
  courseId: string | null;
  courseName: string | null;
  currentUsageCount: number;
  isExpired: boolean;
}

interface Course {
  id: string;
  courseName: string;
}

interface SortConfig {
  key: keyof PromoCode;
  direction: 'asc' | 'desc';
}

const PromoCodes: React.FC = () => {
  const { isModerator } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  const [promoCodeToDelete, setPromoCodeToDelete] = useState<PromoCode | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form data for single promocode
  const [formData, setFormData] = useState({
    code: '',
    type: 'SingleUse' as 'SingleUse' | 'MultiUse',
    discountType: 'Percentage' as 'Percentage' | 'FixedAmount',
    discountValue: '',
    expirationDate: '',
    maxGlobalUsage: '',
    maxUsagePerStudent: '',
    isActive: true,
    description: '',
    courseId: '',
  });

  // Form data for batch promocodes
  const [batchFormData, setBatchFormData] = useState({
    count: '',
    codePrefix: '',
    type: 'SingleUse' as 'SingleUse' | 'MultiUse',
    discountType: 'Percentage' as 'Percentage' | 'FixedAmount',
    discountValue: '',
    expirationDate: '',
    description: '',
    courseId: '',
  });

  // Fetch courses for dropdown
  const fetchCourses = async () => {
    try {
      const response = await authFetch(apiUrl('Course/all?pageNumber=1&pageSize=1000'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCourses(data.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch promocodes from API
  const fetchPromoCodes = async () => {
    if (!isModerator()) {
      setError('You do not have permission to view promocodes.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await promoCodesApi.getAllPromoCodes(currentPage, itemsPerPage);

      if (response.success) {
        const promoCodeData = response.data;
        setPromoCodes(promoCodeData.data || []);
        setTotalCount(promoCodeData.totalCount || 0);
        setTotalPages(promoCodeData.totalPages || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch promocodes');
      }
    } catch (error) {
      console.error('Error fetching promocodes:', error);
      setError('Failed to load promocodes. Please try again later.');
      setPromoCodes([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Sorting logic
  const handleSort = (key: keyof PromoCode) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting
  const sortedPromoCodes = useMemo(() => {
    let sortablePromoCodes = [...promoCodes];
    if (sortConfig !== null) {
      sortablePromoCodes.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortablePromoCodes;
  }, [promoCodes, sortConfig]);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle batch form input change
  const handleBatchInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setBatchFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle create promocode
  const handleCreatePromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      // Convert type and discountType to integer values as expected by the API
      // type: 1 = MultiUse, 2 = SingleUse
      // discountType: 1 = FixedAmount, 2 = Percentage
      const typeValue = formData.type === 'MultiUse' ? 1 : 2;
      const discountTypeValue = formData.discountType === 'FixedAmount' ? 1 : 2;

      const promoCodeData: any = {
        code: formData.code.toUpperCase(),
        type: typeValue,
        discountType: discountTypeValue,
        discountValue: parseFloat(formData.discountValue),
        expirationDate: new Date(formData.expirationDate).toISOString(),
        isActive: formData.isActive,
        description: formData.description || null,
        courseId: formData.courseId ? formData.courseId : null,
      };

      // For MultiUse, include maxGlobalUsage and maxUsagePerStudent if provided
      // For SingleUse, don't include these properties at all (not even as null)
      if (formData.type === 'MultiUse') {
        if (formData.maxGlobalUsage) {
          promoCodeData.maxGlobalUsage = parseInt(formData.maxGlobalUsage);
        }
        if (formData.maxUsagePerStudent) {
          promoCodeData.maxUsagePerStudent = parseInt(formData.maxUsagePerStudent);
        }
      }
      // For SingleUse, we don't add maxGlobalUsage or maxUsagePerStudent to the payload

      const response = await promoCodesApi.createPromoCode(promoCodeData);

      if (response.success) {
        setSuccessMessage('تم إنشاء كود الخصم بنجاح');
        setShowAddForm(false);
        resetForm();
        fetchPromoCodes();
        
        // Export the newly created promocode
        if (response.data) {
          const newPromoCode = response.data;
          const fileName = `promo-code-${newPromoCode.code}-${new Date().toISOString().split('T')[0]}.xlsx`;
          exportPromoCodesToExcel([newPromoCode], fileName);
        }
        
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const errorMessage = response.message || response.error || 'Failed to create promocode';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('Error creating promocode:', error);
      let errorMessage = 'Failed to create promocode. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    }
  };

  // Handle batch create promocodes
  const handleBatchCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      // Convert type and discountType to integer values as expected by the API
      // type: 1 = MultiUse, 2 = SingleUse
      // discountType: 1 = FixedAmount, 2 = Percentage
      const typeValue = batchFormData.type === 'MultiUse' ? 1 : 2;
      const discountTypeValue = batchFormData.discountType === 'FixedAmount' ? 1 : 2;

      const batchData: any = {
        count: parseInt(batchFormData.count),
        type: typeValue,
        discountType: discountTypeValue,
        discountValue: parseFloat(batchFormData.discountValue),
        expirationDate: new Date(batchFormData.expirationDate).toISOString(),
        description: batchFormData.description || null,
        courseId: batchFormData.courseId || null,
      };

      if (batchFormData.codePrefix) {
        batchData.codePrefix = batchFormData.codePrefix.toUpperCase();
      }

      const response = await promoCodesApi.createPromoCodeBatch(batchData);

      if (response.success) {
        setSuccessMessage(`تم إنشاء ${batchFormData.count} كود خصم بنجاح`);
        setShowBatchForm(false);
        resetBatchForm();
        fetchPromoCodes();
        
        // Export the newly created promocodes
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          const newPromoCodes = response.data;
          const prefix = batchFormData.codePrefix || 'promo-codes';
          const fileName = `${prefix}-${new Date().toISOString().split('T')[0]}.xlsx`;
          exportPromoCodesToExcel(newPromoCodes, fileName);
        }
        
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || 'Failed to create batch promocodes');
      }
    } catch (error: any) {
      console.error('Error creating batch promocodes:', error);
      setError(error.message || 'Failed to create batch promocodes. Please try again.');
    }
  };

  // Handle update promocode
  const handleUpdatePromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPromoCode) return;

    setError(null);
    setSuccessMessage(null);

    try {
      // Convert type and discountType to integer values as expected by the API
      // type: 1 = MultiUse, 2 = SingleUse
      // discountType: 1 = FixedAmount, 2 = Percentage
      const typeValue = formData.type === 'MultiUse' ? 1 : 2;
      const discountTypeValue = formData.discountType === 'FixedAmount' ? 1 : 2;

      const promoCodeData: any = {
        code: formData.code.toUpperCase(),
        type: typeValue,
        discountType: discountTypeValue,
        discountValue: parseFloat(formData.discountValue),
        expirationDate: new Date(formData.expirationDate).toISOString(),
        isActive: formData.isActive,
        description: formData.description || null,
        courseId: formData.courseId ? formData.courseId : null,
      };

      // For MultiUse, include maxGlobalUsage and maxUsagePerStudent if provided
      // For SingleUse, don't include these properties at all (not even as null)
      if (formData.type === 'MultiUse') {
        if (formData.maxGlobalUsage) {
          promoCodeData.maxGlobalUsage = parseInt(formData.maxGlobalUsage);
        }
        if (formData.maxUsagePerStudent) {
          promoCodeData.maxUsagePerStudent = parseInt(formData.maxUsagePerStudent);
        }
      }
      // For SingleUse, we don't add maxGlobalUsage or maxUsagePerStudent to the payload

      const response = await promoCodesApi.updatePromoCode(editingPromoCode.id, promoCodeData);

      if (response.success) {
        setSuccessMessage('تم تحديث كود الخصم بنجاح');
        setEditingPromoCode(null);
        resetForm();
        fetchPromoCodes();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || 'Failed to update promocode');
      }
    } catch (error: any) {
      console.error('Error updating promocode:', error);
      setError(error.message || 'Failed to update promocode. Please try again.');
    }
  };

  // Handle delete promocode
  const handleDeletePromoCode = async () => {
    if (!promoCodeToDelete) return;

    setIsDeleting(true);
    setError(null);
    try {
      const response = await promoCodesApi.deletePromoCode(promoCodeToDelete.id);

      if (response.success) {
        setSuccessMessage('تم حذف كود الخصم بنجاح');
        setPromoCodeToDelete(null);
        fetchPromoCodes();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || 'Failed to delete promocode');
      }
    } catch (error: any) {
      console.error('Error deleting promocode:', error);
      setError(error.message || 'Failed to delete promocode. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      code: '',
      type: 'SingleUse',
      discountType: 'Percentage',
      discountValue: '',
      expirationDate: '',
      maxGlobalUsage: '',
      maxUsagePerStudent: '',
      isActive: true,
      description: '',
      courseId: '',
    });
  };

  // Reset batch form
  const resetBatchForm = () => {
    setBatchFormData({
      count: '',
      codePrefix: '',
      type: 'SingleUse',
      discountType: 'Percentage',
      discountValue: '',
      expirationDate: '',
      description: '',
      courseId: '',
    });
  };

  // Load promocode for editing
  const loadPromoCodeForEdit = (promoCode: PromoCode) => {
    setEditingPromoCode(promoCode);
    fetchCourses();
    // Convert integer type/discountType from API to string for form
    // type: 1 = MultiUse, 2 = SingleUse
    // discountType: 1 = FixedAmount, 2 = Percentage
    const typeString = (promoCode.type as any) === 1 || promoCode.type === 'MultiUse' ? 'MultiUse' : 'SingleUse';
    const discountTypeString = (promoCode.discountType as any) === 1 || promoCode.discountType === 'FixedAmount' ? 'FixedAmount' : 'Percentage';
    
    setFormData({
      code: promoCode.code,
      type: typeString as 'SingleUse' | 'MultiUse',
      discountType: discountTypeString as 'Percentage' | 'FixedAmount',
      discountValue: promoCode.discountValue.toString(),
      expirationDate: new Date(promoCode.expirationDate).toISOString().split('T')[0],
      maxGlobalUsage: promoCode.maxGlobalUsage?.toString() || '',
      maxUsagePerStudent: promoCode.maxUsagePerStudent?.toString() || '',
      isActive: promoCode.isActive,
      description: promoCode.description || '',
      courseId: promoCode.courseId || '',
    });
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Export promocodes to Excel (helper function)
  const exportPromoCodesToExcel = (promoCodesToExport: PromoCode[], fileName: string) => {
    // Prepare data for Excel
    const excelData = promoCodesToExport.map((promoCode: PromoCode) => {
      const typeValue = (promoCode.type === 'SingleUse' || promoCode.type === 2) ? 'استخدام واحد' : 'استخدام متعدد';
      const discountTypeValue = (promoCode.discountType === 'Percentage' || promoCode.discountType === 2) ? 'نسبة مئوية' : 'مبلغ ثابت';
      const discountDisplay = (promoCode.discountType === 'Percentage' || promoCode.discountType === 2)
        ? `${promoCode.discountValue}%`
        : `${promoCode.discountValue} ريال`;
      
      return {
        'الكود': promoCode.code,
        'النوع': typeValue,
        'نوع الخصم': discountTypeValue,
        'قيمة الخصم': discountDisplay,
        'تاريخ الانتهاء': formatDate(promoCode.expirationDate),
        'الحد الأقصى للاستخدام العام': promoCode.maxGlobalUsage || '-',
        'الحد الأقصى لكل طالب': promoCode.maxUsagePerStudent || '-',
        'عدد الاستخدامات': promoCode.currentUsageCount,
        'نشط': promoCode.isActive ? 'نعم' : 'لا',
        'منتهي': promoCode.isExpired ? 'نعم' : 'لا',
        'الدورة': stripSarPrice(promoCode.courseName) || 'جميع الدورات',
        'الوصف': promoCode.description || '-',
        'تاريخ الإنشاء': formatDate(promoCode.createdDate),
        'تم الإنشاء بواسطة': promoCode.createdBy,
      };
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'أكواد الخصم');

    // Set column widths
    const colWidths = [
      { wch: 20 }, // الكود
      { wch: 15 }, // النوع
      { wch: 15 }, // نوع الخصم
      { wch: 15 }, // قيمة الخصم
      { wch: 20 }, // تاريخ الانتهاء
      { wch: 25 }, // الحد الأقصى للاستخدام العام
      { wch: 20 }, // الحد الأقصى لكل طالب
      { wch: 15 }, // عدد الاستخدامات
      { wch: 10 }, // نشط
      { wch: 10 }, // منتهي
      { wch: 25 }, // الدورة
      { wch: 30 }, // الوصف
      { wch: 20 }, // تاريخ الإنشاء
      { wch: 20 }, // تم الإنشاء بواسطة
    ];
    ws['!cols'] = colWidths;

    // Generate Excel file
    XLSX.writeFile(wb, fileName);
  };

  if (!isModerator()) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to view promocodes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" dir="rtl">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold">أكواد الخصم</h1>
        <div className="flex gap-2">
          <button
            onClick={() => {
              resetForm();
              setShowAddForm(true);
              setEditingPromoCode(null);
              fetchCourses();
            }}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5 ml-2" />
            إضافة كود خصم
          </button>
          <button
            onClick={() => {
              resetBatchForm();
              setShowBatchForm(true);
              fetchCourses();
            }}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5 ml-2" />
            إنشاء مجموعة
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="mr-3">
              <h3 className="text-sm font-medium text-red-800">خطأ</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {successMessage && (
        <div className="mb-6 rounded-lg bg-green-50 p-4 border border-green-200">
          <div className="flex">
            <div className="mr-3">
              <h3 className="text-sm font-medium text-green-800">نجح</h3>
              <p className="mt-1 text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* PromoCodes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <span className="font-medium">الإجمالي: {totalCount}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {([
                  { key: 'code' as const, label: 'الكود' },
                  { key: 'type' as const, label: 'النوع' },
                  { key: 'discountType' as const, label: 'نوع الخصم' },
                  { key: 'discountValue' as const, label: 'قيمة الخصم' },
                  { key: 'expirationDate' as const, label: 'تاريخ الانتهاء' },
                  { key: 'maxGlobalUsage' as const, label: 'الحد الأقصى للاستخدام' },
                  { key: 'currentUsageCount' as const, label: 'عدد الاستخدامات' },
                  { key: 'isActive' as const, label: 'نشط' },
                  { key: 'isExpired' as const, label: 'منتهي' },
                  { key: 'courseName' as const, label: 'الدورة' },
                  { key: 'actions' as const, label: 'الإجراءات' }
                ] as const).map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => key !== 'actions' && handleSort(key as keyof PromoCode)}
                    className={`px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      key !== 'actions' ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''
                    }`}
                  >
                    {label}
                    {sortConfig && sortConfig.key === key as keyof PromoCode && key !== 'actions' && (
                      <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-6 py-12">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedPromoCodes.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-4 text-center text-gray-500">
                    لا توجد أكواد خصم
                  </td>
                </tr>
              ) : (
                sortedPromoCodes.map((promoCode) => (
                  <tr key={promoCode.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-sm">{promoCode.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {(promoCode.type === 'SingleUse' || promoCode.type === 2) ? 'استخدام واحد' : 'استخدام متعدد'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {(promoCode.discountType === 'Percentage' || promoCode.discountType === 2) ? 'نسبة مئوية' : 'مبلغ ثابت'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {(promoCode.discountType === 'Percentage' || promoCode.discountType === 2)
                        ? `${promoCode.discountValue}%` 
                        : `${promoCode.discountValue} ريال`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(promoCode.expirationDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {promoCode.maxGlobalUsage || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{promoCode.currentUsageCount}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          promoCode.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {promoCode.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          promoCode.isExpired
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {promoCode.isExpired ? 'منتهي' : 'صالح'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {stripSarPrice(promoCode.courseName) || 'جميع الدورات'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => loadPromoCodeForEdit(promoCode)}
                          className="text-blue-600 hover:text-blue-900"
                          title="تعديل"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setPromoCodeToDelete(promoCode)}
                          className="text-red-600 hover:text-red-900"
                          title="حذف"
                          disabled={promoCode.currentUsageCount > 0}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 0 && (
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="mb-4 sm:mb-0">
              <p className="text-sm text-gray-700">
                عرض <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> إلى{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalCount)}
                </span>{' '}
                من <span className="font-medium">{totalCount}</span> نتيجة
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&laquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&lsaquo;</span>
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    if (index > 0 && prevPage !== page - 1) {
                      return [
                        <span key={`ellipsis-${prevPage}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>,
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                            currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ];
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                          currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }).flat()}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&rsaquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span>&raquo;</span>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit PromoCode Modal */}
      {(showAddForm || editingPromoCode) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200">
            <h2 className="text-xl font-bold mb-6">
              {editingPromoCode ? 'تعديل كود الخصم' : 'إضافة كود خصم جديد'}
            </h2>
            <form onSubmit={editingPromoCode ? handleUpdatePromoCode : handleCreatePromoCode}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الكود *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    placeholder="مثال: SUMMER2024"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">النوع *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="SingleUse">استخدام واحد</option>
                    <option value="MultiUse">استخدام متعدد</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع الخصم *</label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="Percentage">نسبة مئوية (%)</option>
                    <option value="FixedAmount">مبلغ ثابت (ريال)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">قيمة الخصم *</label>
                  <input
                    type="number"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    min="0"
                    max={formData.discountType === 'Percentage' ? '100' : undefined}
                    step="0.01"
                    placeholder={formData.discountType === 'Percentage' ? '0-100' : '0.00'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الانتهاء *</label>
                  <input
                    type="datetime-local"
                    name="expirationDate"
                    value={formData.expirationDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                {formData.type === 'MultiUse' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">الحد الأقصى للاستخدام العام</label>
                      <input
                        type="number"
                        name="maxGlobalUsage"
                        value={formData.maxGlobalUsage}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="1"
                        placeholder="اختياري"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">الحد الأقصى للاستخدام لكل طالب</label>
                      <input
                        type="number"
                        name="maxUsagePerStudent"
                        value={formData.maxUsagePerStudent}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="1"
                        placeholder="اختياري"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الدورة</label>
                  <select
                    name="courseId"
                    value={formData.courseId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">جميع الدورات</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {stripSarPrice(course.courseName)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الوصف</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="وصف اختياري لكود الخصم"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="mr-2 block text-sm text-gray-700">نشط</label>
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingPromoCode(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingPromoCode ? 'تحديث' : 'إنشاء'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch Create Modal */}
      {showBatchForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200">
            <h2 className="text-xl font-bold mb-6">إنشاء مجموعة أكواد خصم</h2>
            <form onSubmit={handleBatchCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">عدد الأكواد *</label>
                  <input
                    type="number"
                    name="count"
                    value={batchFormData.count}
                    onChange={handleBatchInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    min="1"
                    placeholder="مثال: 10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">بادئة الكود (اختياري)</label>
                  <input
                    type="text"
                    name="codePrefix"
                    value={batchFormData.codePrefix}
                    onChange={handleBatchInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="مثال: SUMMER"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">النوع *</label>
                  <select
                    name="type"
                    value={batchFormData.type}
                    onChange={handleBatchInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="SingleUse">استخدام واحد</option>
                    <option value="MultiUse">استخدام متعدد</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع الخصم *</label>
                  <select
                    name="discountType"
                    value={batchFormData.discountType}
                    onChange={handleBatchInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="Percentage">نسبة مئوية (%)</option>
                    <option value="FixedAmount">مبلغ ثابت (ريال)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">قيمة الخصم *</label>
                  <input
                    type="number"
                    name="discountValue"
                    value={batchFormData.discountValue}
                    onChange={handleBatchInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    min="0"
                    max={batchFormData.discountType === 'Percentage' ? '100' : undefined}
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الانتهاء *</label>
                  <input
                    type="datetime-local"
                    name="expirationDate"
                    value={batchFormData.expirationDate}
                    onChange={handleBatchInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الدورة</label>
                  <select
                    name="courseId"
                    value={batchFormData.courseId}
                    onChange={handleBatchInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">جميع الدورات</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {stripSarPrice(course.courseName)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الوصف</label>
                  <textarea
                    name="description"
                    value={batchFormData.description}
                    onChange={handleBatchInputChange}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="وصف اختياري"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowBatchForm(false);
                    resetBatchForm();
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  إنشاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {promoCodeToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200">
            <div className="text-center mb-5">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">حذف كود الخصم</h3>
              <p className="text-gray-600">
                هل أنت متأكد من حذف كود الخصم <span className="font-bold">{promoCodeToDelete.code}</span>؟
                {promoCodeToDelete.currentUsageCount > 0 && (
                  <span className="block mt-2 text-red-600">
                    لا يمكن حذف كود الخصم الذي تم استخدامه بالفعل.
                  </span>
                )}
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setPromoCodeToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleDeletePromoCode}
                disabled={isDeleting || promoCodeToDelete.currentUsageCount > 0}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    جاري الحذف...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4" />
                    حذف
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodes;
